import {
    Component,
    ChangeDetectionStrategy,
    Directive,
    ElementRef,
    HostListener,
    Input,
    ViewContainerRef,
    inject,
    OnDestroy,
} from '@angular/core'
import { Overlay, OverlayRef, ConnectedPosition } from '@angular/cdk/overlay'
import { ComponentPortal } from '@angular/cdk/portal'

type EiTooltipPosition = 'top' | 'bottom' | 'left' | 'right'

@Component({
    selector: 'app-ei-tooltip-overlay',
    standalone: true,
    template: `
        <div class="ei-tooltip" role="tooltip" [attr.data-side]="side">
            {{ text }}
        </div>
    `,
    styles: [
        `
            .ei-tooltip {
                max-width: 320px;
                padding: 8px 10px;
                border-radius: 10px;
                background: rgba(16, 16, 16, 0.96);
                color: rgba(255, 255, 255, 0.92);
                font-size: 12px;
                font-weight: 500;
                line-height: 1.2;
                box-shadow: 0 8px 22px rgba(0, 0, 0, 0.28);
                border: 1px solid rgba(255, 255, 255, 0.08);
                pointer-events: none;
                white-space: normal;
                word-break: break-word;
            }
        `,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EiTooltipOverlayComponent {
    text = ''
    side: EiTooltipPosition = 'top'
}

@Directive({
    selector: '[eiTooltip]',
    standalone: true,
})
export class EiTooltipDirective implements OnDestroy {
    private readonly overlay = inject(Overlay)
    private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef)
    private readonly vcr = inject(ViewContainerRef)

    private overlayRef: OverlayRef | null = null
    private isOpen = false

    @Input('eiTooltip') text: string | null = null
    @Input() eiTooltipPosition: EiTooltipPosition = 'top'

    @HostListener('mouseenter')
    @HostListener('focusin')
    onPointerEnter() {
        if (this.isOpen || !this.text) return
        this.open()
    }

    @HostListener('mouseleave')
    @HostListener('focusout')
    onPointerLeave() {
        if (!this.isOpen) return
        this.close()
    }

    private open() {
        const positions = this.getPositions(this.eiTooltipPosition)

        this.overlayRef =
            this.overlayRef ??
            this.overlay.create({
                positionStrategy: this.overlay
                    .position()
                    .flexibleConnectedTo(this.elementRef)
                    .withFlexibleDimensions(false)
                    .withPush(true)
                    .withViewportMargin(8)
                    .withPositions(positions),
                scrollStrategy: this.overlay.scrollStrategies.reposition(),
                hasBackdrop: false,
            })

        const portal = new ComponentPortal(EiTooltipOverlayComponent, this.vcr)
        const componentRef = this.overlayRef.attach(portal)
        componentRef.instance.text = this.text ?? ''
        componentRef.instance.side = this.eiTooltipPosition

        this.isOpen = true
    }

    private close() {
        this.overlayRef?.detach()
        this.isOpen = false
    }

    ngOnDestroy(): void {
        this.overlayRef?.detach()
        this.overlayRef?.dispose()
        this.overlayRef = null
        this.isOpen = false
    }

    private getPositions(preferred: EiTooltipPosition): ConnectedPosition[] {
        const top: ConnectedPosition = {
            originX: 'center',
            originY: 'top',
            overlayX: 'center',
            overlayY: 'bottom',
            offsetY: -8,
        }
        const bottom: ConnectedPosition = {
            originX: 'center',
            originY: 'bottom',
            overlayX: 'center',
            overlayY: 'top',
            offsetY: 8,
        }
        const left: ConnectedPosition = {
            originX: 'start',
            originY: 'center',
            overlayX: 'end',
            overlayY: 'center',
            offsetX: -8,
        }
        const right: ConnectedPosition = {
            originX: 'end',
            originY: 'center',
            overlayX: 'start',
            overlayY: 'center',
            offsetX: 8,
        }

        switch (preferred) {
            case 'bottom':
                return [bottom, top, right, left]
            case 'left':
                return [left, right, top, bottom]
            case 'right':
                return [right, left, top, bottom]
            case 'top':
            default:
                return [top, bottom, right, left]
        }
    }
}
